import React, { useEffect, useCallback, useState, useMemo, memo } from "react";
import { Box, Grid2, styled, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import DigitalTwinInfoMatrixList from "./DigitalTwinInfoMatrixList";
import { getTruncatedName } from "../../Helper/Formatters/TextFormatter";
import { getDigitalTwinBatteryTags, getDigitalTwinRealTimeMatrics } from "../../Api/DigitalTwin";
import { getMetricTimeStamp } from "../../Helper/DatePicker/DateConverter";
import CartLoader from "../../Helper/Loaders/CartLoader";
import { isNumber, normalizeKey } from "../../Helper/Common/common_helpers";
import { useNavigate } from "react-router-dom";
import { useBatteryInfo, useVehicleInfo, useBatteryConfig } from "../../Pages/DigitalTwinV3/DigitalTwinContext";
import { encryptTheParams, decryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import DefaultInfo from "./components/Header/DefaultInfo";
import BusDeviceInfo from "./components/Header/BusDeviceInfo";
import ConfigurableDeviceInfo from "./components/Header/ConfigurableDeviceInfo";
import { orderHeaderMetrics } from "./components/Header/headerConfig";
import OperationalStatusAction from "../../Components/OperationalStatus/OperationalStatusAction";
import { CanAny } from "../../Components/PermissionGuards";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
import { usePermissions } from "../../Context/PermissionContext";
import { replacementCheckVehicleIds } from "./Tabs/BatteryHealth/packReplacementAnnotations";
import EventHistory from "./EventHistory";
import { usePagesContent } from "../../Hooks/usePagesContent";
import { useEntityOperationalStatus } from "../../Hooks/useEntityOperationalStatus";
import { isFieldIndicatorsEnabled } from "../../Components/FieldStatusIndicator";

const PageHeaderContainer = styled(Grid2)(({ theme, color }) => ({
    padding: '16px 24px 20px 24px',
    borderRadius: '12px',
    border: '1px solid #E9E9E9',
    background: '#FFF',
    marginBottom: '8px',
}));

const MetricDisplayBox = memo(({ metric }) => {
    const { t } = useTranslation();
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', whiteSpace: 'nowrap', minWidth: 'fit-content' }}>
            <Typography sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '16px' }}
                variant="fw500s12lh24cA2A2A2"> {getTruncatedName(t(metric.label), 60)}</Typography>
            <Typography
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '8px' }}
                variant="fw500s14lh24c444B51">
                {metric.value}
            </Typography>
        </Box>
    );
});

const formatMetricValue = (metricData, key) => {
    const { value, unit = "" } = metricData;
    if (!value || ["NA", "na"].includes(value)) return "NA";
    if (key === "addedOn") return getMetricTimeStamp(value, "DD MMM YYYY hh:MM A");
    if (["sohEst", "eqCycleEst", "minSOHBMS", "odometer", "ext_trip_distance"].includes(key)) {
        const numeric = parseFloat(value);
        return isNaN(numeric) ? "NA" : `${numeric.toFixed(0)} ${unit}`;
    }
    if (["balanceState"].includes(key)) {
        const numeric = parseFloat(value);
        return isNaN(numeric) ? "NA" : `${numeric.toFixed(2)} ${unit}`;
    }
    return `${value} ${unit}`.trim();
};

const getSOCColors = (soc) => {
    if (!isNumber(soc) || soc === "NA") {
        return {
            soc_bg_color: '#FFEEE8',
            soc_text_color: '#FF4D00'
        };
    }

    const socValue = Number(soc);

    if (socValue < 30) {
        return {
            soc_bg_color: '#FFE4E4',
            soc_text_color: '#2E2E2E'
        };
    }

    if (socValue <= 50) {
        return {
            soc_bg_color: '#FEF1C6',
            soc_text_color: '#2E2E2E'
        };
    }

    return {
        soc_bg_color: '#EEF4DB',
        soc_text_color: '#2E2E2E'
    };
};


function DigitalTwinHeader() {
    const { batteryID, batteryInfo, showBusDeviceInfo, pageType, operationalStatusVersion, refreshOperationalStatus } = useBatteryInfo()
    const vehicleInfo = useVehicleInfo();
    const batteryConfig = useBatteryConfig();
    const navigate = useNavigate();
    const { hasPermission, isRbacV2Enabled } = usePermissions();

    // Numeric vehicleID (from the vehicle info, else the battery config) powers
    // the operational-status "replaced during downtime?" cross-check.
    const vehicleId = vehicleInfo?.vehicleID ?? batteryConfig?.vehicleID;
    const handleRecordNow = () => {
        encryptTheParams(
            { ...decryptTheParams(), batteryID, tabValue: 'components' },
            navigate,
            false,
            '/specificBattery',
            true
        );
    };
    const pageContent = usePagesContent();
    // Asked only when the user can record a pack replacement (Components tab gate).
    const replacementVehicleIds = replacementCheckVehicleIds({
        vehicleId,
        tabsToShow: pageContent?.metaInfo?.tabsToShow,
        isRbacV2: isRbacV2Enabled(),
        hasPermission,
    });

    // Operational status for the bus-icon status marker. Fetched once here and shared:
    // the marker reads it, and OperationalStatusAction takes it as preloadedTags instead
    // of repeating the request.
    //
    // Gated on the feature flag as well as permission and page type, so an org with the
    // feature off makes exactly the requests it made before this existed - and, because
    // preloadedTags is then left undefined, the action fetches its own tags as it always
    // did rather than waiting forever for a preload that will never arrive.
    const indicatorsEnabled = isFieldIndicatorsEnabled(pageContent?.metaInfo);
    const canViewOperationalStatus =
        pageType !== 'IOTDigitalTwin' &&
        (!isRbacV2Enabled() || hasPermission(FEATURE_CODES.OPERATIONAL_STATUS, 'view') ||
            hasPermission(FEATURE_CODES.OPERATIONAL_STATUS, 'update'));
    const operationalStatus = useEntityOperationalStatus(batteryID, {
        entityType: 'battery',
        version: operationalStatusVersion,
        enabled: indicatorsEnabled && canViewOperationalStatus,
    });
    const headerConfig = pageContent?.metaInfo?.header || null;
    const canUpdateOperationalStatus = !isRbacV2Enabled() || hasPermission(FEATURE_CODES.OPERATIONAL_STATUS, 'update');
    const pageSpecificFeatures = pageContent?.metaInfo?.enabledFeatures;
    const globalEnabledFeatures = pageContent?.metaInfo?.globalConfig?.enabledFeatures || [];
    const enabledFeatures = pageSpecificFeatures ?? globalEnabledFeatures;
    const isBatteryHistoryEnabled = enabledFeatures.includes('DigitalTwin.BatteryHistory');
    const [isLoading, setIsLoading] = useState(true);
    const { t } = useTranslation(['common', 'digital_twin'])
    const [batteryTags, setBatteryTags] = useState({ soc: {}, active_alerts: {} });
    const [metricList, setMetricList] = useState([]);

    // null until the user picks metrics with the pencil: the default is the first four of
    // the configured order (header.metrics), else of the API order.
    const [selectedMetrics, setSelectedMetrics] = useState(null);
    const configuredMetrics = headerConfig?.metrics;
    // The vehicle context object is rebuilt on every provider render: key the memo on the
    // vehicle values the configured metrics read, so the lists keep their identity (the
    // pencil popover resets its unapplied picks whenever selectedMetrics changes identity).
    const vehicleMetricValues = JSON.stringify(
        (configuredMetrics || []).filter((m) => m?.source === "vehicleInfo").map((m) => vehicleInfo?.[m.key] ?? null)
    );
    const orderedMetrics = useMemo(
        () => orderHeaderMetrics(metricList, configuredMetrics, { vehicleInfo }, formatMetricValue),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- vehicleInfo is tracked through vehicleMetricValues
        [metricList, configuredMetrics, vehicleMetricValues]
    );
    const shownMetrics = useMemo(() => selectedMetrics ?? orderedMetrics.slice(0, 4), [selectedMetrics, orderedMetrics]);

    const applySelectedMetric = useCallback((metrics) => {
        setSelectedMetrics(metrics);
    }, []);


    const fetchData = useCallback(async () => {
        setIsLoading(true);
        let realTimeMatricsParams = {};
        if (showBusDeviceInfo()) {
            realTimeMatricsParams = { embed: 'VEHICLE_INFO' }
        }
        try {
            const [tagsRes, metricsRes] = await Promise.all([
                getDigitalTwinBatteryTags(batteryID, { tags: ["soc", "active_alerts", "active_diagnostics"] }),
                getDigitalTwinRealTimeMatrics(batteryID, realTimeMatricsParams),
            ]);
            setBatteryTags(tagsRes.data);

            const metrics = Object.entries(metricsRes.data).map(([key, data]) => ({
                key,
                ...data,
                value: formatMetricValue(data, key),
                label: t(`digital_twin:live_metrics.${normalizeKey(data.label)}`, data.label)
            }));

            setMetricList(metrics);
            setSelectedMetrics(null);
        } catch (err) {
            console.error("Failed to fetch Digital Twin data", err);
        } finally {
            setIsLoading(false);
        }
    }, [batteryID, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);


    if (isLoading) {
        return (
            <PageHeaderContainer container justifyContent={"center"} alignItems={"center"} sx={{ height: '154px' }}>
                <CartLoader />
            </PageHeaderContainer>
        );
    }

    return (
        <PageHeaderContainer container justifyContent={'space-between'}>
            <Grid2 container flexDirection={'column'}>
                {headerConfig ? (
                    <ConfigurableDeviceInfo headerConfig={headerConfig} batteryInfo={batteryInfo} batteryTags={batteryTags} getSOCColors={getSOCColors} operationalStatus={operationalStatus} />
                ) : showBusDeviceInfo() ? (
                    <BusDeviceInfo batteryInfo={batteryInfo} batteryTags={batteryTags} getSOCColors={getSOCColors} operationalStatus={operationalStatus} />
                ) : (
                    // No marker here: DefaultInfo is the battery-centric header, which
                    // shows no vehicle identity to hang an operational status off.
                    <DefaultInfo batteryInfo={batteryInfo} batteryTags={batteryTags} getSOCColors={getSOCColors} />
                )}
            </Grid2>

            <Grid2 container flexDirection={'column'} alignItems={'flex-end'} justifyContent={'space-between'}>
                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
                    {pageType !== 'IOTDigitalTwin' && (
                        <CanAny feature={FEATURE_CODES.OPERATIONAL_STATUS} operations={['view', 'update']}>
                            <OperationalStatusAction
                                entityIds={[batteryID]}
                                isActive={true}
                                entityType={'battery'}
                                vehicleNumber={vehicleInfo?.licensePlateNo}
                                deviceID={batteryInfo?.deviceID}
                                readOnly={!canUpdateOperationalStatus}
                                vehicleIds={replacementVehicleIds}
                                onRecordNow={handleRecordNow}
                                onUpdate={refreshOperationalStatus}
                                // Only when the marker is actually in play; otherwise
                                // undefined, which tells the action to fetch its own.
                                preloadedTags={
                                    indicatorsEnabled && canViewOperationalStatus
                                        ? operationalStatus.tags
                                        : undefined
                                }
                            />
                        </CanAny>
                    )}
                    {isBatteryHistoryEnabled && <EventHistory batteryID={batteryID} />}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
                    <Grid2 container gap={3.5}>
                        {shownMetrics.map((metric, index) => (<MetricDisplayBox key={index} metric={metric} />))}
                    </Grid2>
                    <DigitalTwinInfoMatrixList
                        selectedMetrics={shownMetrics}
                        applySelectedMetric={applySelectedMetric}
                        metricList={orderedMetrics}
                    />
                </Box>
            </Grid2>
        </PageHeaderContainer >
    );
}

export default DigitalTwinHeader;
